// Singleton owner of the app's AudioContext. Centralizes iOS audio
// session lifecycle: unlock under a user gesture, suspend on page
// hide, resume on page show, surface unexpected suspensions as
// 'interrupted' events (iOS audio-session pre-emption).

const PRIVATE = Symbol('AudioSessionManager.private');
let instance = null;

export default class AudioSessionManager {
  constructor(token) {
    if (token !== PRIVATE) {
      throw new Error(
        'AudioSessionManager constructor is private — use AudioSessionManager.getInstance()'
      );
    }
    this._ctx = null;
    this._state = 'locked';
    this._listeners = new Map(); // event → Set<callback>
    this._everUnlocked = false;
    this._userSuspended = false;
    this._destinationNode = null;
    this._sinkElement = null;
    this._silentSource = null;

    if (typeof document !== 'undefined') {
      document.addEventListener(
        'visibilitychange',
        this._onVisibilityChange
      );
    }
  }

  static getInstance() {
    if (!instance) instance = new AudioSessionManager(PRIVATE);
    return instance;
  }

  getContext() {
    if (!this._ctx) {
      throw new Error(
        'AudioContext not yet created — call unlock() inside a user gesture first'
      );
    }
    return this._ctx;
  }

  getDestinationNode() {
    return this._destinationNode || this._ctx?.destination || null;
  }

  getState() {
    return this._state;
  }

  async unlock() {
    if (!this._ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) {
        throw new Error('Web Audio API not supported in this environment');
      }
      this._ctx = new AC();
      this._ctx.onstatechange = this._onContextStateChange;
    }
    if (this._ctx.state === 'suspended') {
      await this._ctx.resume();
    }
    if (!this._sinkElement) {
      try {
        // Create a MediaStream destination. TTSPlayer connects
        // AudioBufferSourceNodes to this instead of audioContext.destination.
        this._destinationNode = this._ctx.createMediaStreamDestination();

        // Continuous silent source to prevent MediaStream buffer
        // underruns. Without this, the persistent HTMLAudioElement
        // playing srcObject = stream produces periodic clicks/pops on
        // iOS when no real audio is flowing (between TTS plays). A
        // ConstantSourceNode at offset=0 produces a steady stream of
        // zero-amplitude samples, keeping the MediaStream continuously
        // fed. Never stopped — lives for the page lifetime.
        this._silentSource = this._ctx.createConstantSource();
        this._silentSource.offset.value = 0;
        this._silentSource.connect(this._destinationNode);
        this._silentSource.start();

        // Persistent HTMLAudioElement whose source is the MediaStream.
        // HTMLAudioElement.play() is what iOS recognizes as media
        // playback — it claims the audio session for Playback category.
        // AudioContext alone uses Ambient category and produces silent
        // output on iOS first-tap.
        const el = new Audio();
        el.srcObject = this._destinationNode.stream;
        el.playsInline = true;
        el.autoplay = false;
        this._sinkElement = el;

        // Fire-and-forget play inside the user gesture. Element stays
        // "playing" for the page lifetime; audio routed through
        // _destinationNode flows to the speaker.
        el.play().catch((err) => {
          console.warn(
            '[AudioSessionManager] sink element play() rejected:',
            err?.message
          );
        });
      } catch (e) {
        console.warn('[AudioSessionManager] sink setup failed:', e?.message);
        // Fallback: _destinationNode stays null, TTSPlayer falls back to
        // audioContext.destination via getDestinationNode().
      }
    }
    this._userSuspended = false;
    this._everUnlocked = true;
    this._recalcState();
  }

  async suspend() {
    if (!this._ctx) return;
    this._userSuspended = true;
    if (this._ctx.state !== 'suspended') {
      await this._ctx.suspend();
    }
    this._recalcState();
  }

  async resume() {
    if (!this._ctx) return;
    if (this._ctx.state === 'suspended') {
      await this._ctx.resume();
    }
    this._userSuspended = false;
    this._recalcState();
  }

  on(event, callback) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(callback);
  }

  off(event, callback) {
    const set = this._listeners.get(event);
    if (set) set.delete(callback);
  }

  _emit(event, payload) {
    const set = this._listeners.get(event);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error('[AudioSessionManager] listener threw:', e);
      }
    }
  }

  _recalcState() {
    const prev = this._state;
    let next;
    if (!this._ctx) {
      next = 'locked';
    } else if (this._ctx.state === 'running') {
      next = 'unlocked';
    } else if (this._ctx.state === 'suspended') {
      const documentHidden =
        typeof document !== 'undefined' && document.hidden;
      const unexpected =
        this._everUnlocked && !this._userSuspended && !documentHidden;
      next = unexpected ? 'interrupted' : 'suspended';
    } else {
      // 'closed' or anything else — treat as locked
      next = 'locked';
    }
    if (next !== prev) {
      this._state = next;
      this._emit('statechange', next);
      if (next === 'interrupted') this._emit('interrupted', next);
    }
  }

  _onContextStateChange = () => {
    this._recalcState();
  };

  _onVisibilityChange = () => {
    if (!this._ctx || !this._everUnlocked) return;
    if (document.hidden) {
      if (this._ctx.state === 'running') {
        this._userSuspended = true;
        this._ctx.suspend().catch(() => {});
      }
    } else {
      if (this._ctx.state === 'suspended' && this._userSuspended) {
        this._userSuspended = false;
        this._ctx.resume().catch(() => {});
      }
    }
  };
}

export const audioSessionManager = AudioSessionManager.getInstance();
